import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { Button, IconButton } from "@mui/material";
import { TailwindModal } from "../../../../components/TailwindModal";
import { NotesTextarea } from "../../../../components-new";
import { cancelButtonSx, primaryButtonSx } from "./add-source/addSourceStyles";
import { useCreateContext, useGetContext } from "../../tars-hooks";

interface AddContextModalProps {
  open: boolean;
  onClose: () => void;
  notebookId: number | null;
}

const CONTEXT_PLACEHOLDER =
  "This quick note is collected and used as part of our research process...";

const AddContextModal = ({ open, onClose, notebookId }: AddContextModalProps) => {
  const [value, setValue] = useState("");
  const { mutate: createContext } = useCreateContext();
  const { data: contextData } = useGetContext(notebookId, open && notebookId != null);

  useEffect(() => {
    if (!open || contextData === undefined) return;
    const text = (contextData as { context?: string }).context;
    setValue(typeof text === "string" ? text : "");
  }, [open, contextData]);

  const canSubmit = value.trim().length > 0;

  const handleDone = () => {
    if (!notebookId || !canSubmit) return;
    createContext({ notebook_id: notebookId, context: value.trim() });
    setValue("");
    onClose();
  };

  return (
    <TailwindModal isOpen={open}>
      <div className="relative mx-auto flex w-full max-w-[820px] flex-col overflow-hidden rounded-[32px] border border-[#CDCDCD] bg-[#FFFFFF] shadow-2xl">
        <IconButton
          onClick={onClose}
          aria-label="Close"
          sx={{
            position: "absolute",
            top: 24,
            right: 24,
            color: "black",
            "&:hover": { color: "#4b5563", bgcolor: "#E2E2E2" },
          }}
        >
          <X size={20} />
        </IconButton>

        <div className="pb-2 pt-8 text-center">
          <h2 className="text-[19px] font-semibold text-[#1d1d1f]">Add Context</h2>
          <p className="mt-0.5 text-[12px] font-medium uppercase tracking-tight text-gray-500">
            Objective of the Notebook
          </p>
        </div>

        <div className="px-6 pb-4">
          <NotesTextarea
            value={value}
            onChange={setValue}
            placeholder={CONTEXT_PLACEHOLDER}
            containerClassName="h-[500px]"
            textareaClassName="h-full resize-none"
          />
        </div>

        <div className="flex items-center justify-end gap-3 px-10 py-6">
          <Button variant="outlined" onClick={onClose} sx={cancelButtonSx}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleDone} disabled={!canSubmit} sx={primaryButtonSx}>
            Done
          </Button>
        </div>
      </div>
    </TailwindModal>
  );
};

export default AddContextModal;
